import { useContext, useCallback, useMemo, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import { object } from 'prop-types'
import { CvsTextField, CvsAutoComplete } from '@components'
import { connectorContext } from '@contexts'
import { useAppSelector } from '@redux/hooks'
import { NameValidator } from './name-validator'
import { validateTableName } from './utils'
import { templateCopyContext } from '../../context'
import { FileFormPartial } from '../file-form-partial'
import { NotificationEmailConfig } from '../notification-email-config'

const FullWidth = styled.div`
  width: 100%;
`
const AutoComplete = styled(CvsAutoComplete)`
  margin-top: -8px;
  margin-right: 12px;
`

export const SnowflakeOutbound = ({ integration, assignment }) => {
  const [showValidation, setShowValidation] = useState(false)
  const { onChange } = useContext(templateCopyContext)
  const { byId } = useContext(connectorContext)
  const template = useAppSelector(state => state.Template)
  const showIntegrationNotifications = useAppSelector(
    state => state.Auth.features['integration-notifications'],
  )
  const connectors = useMemo(
    () =>
      Object.values(byId)
        .filter(
          connector =>
            connector.connector_type_id ===
            (assignment.connector_type_id
              ? assignment.connector_type_id
              : byId[assignment.connector_id].connector_type_id),
        )
        .map(connector => ({ value: connector.id, label: connector.name })),
    [byId, assignment],
  )

  /**
   * If the table name is not set, we use the template name as the table name.
   * Because of that, we don't want to validate the table name, because
   * we are sanitizing it. We only want to validate it when the user starts typing.
   */
  const usingTemplateNameAsTableName = typeof assignment.metadata?.tableName === 'undefined'

  const { errors: validationErrors = [] } = useMemo(() => {
    if (usingTemplateNameAsTableName) return { errors: [] }

    return validateTableName(assignment?.metadata?.tableName)
  }, [assignment?.metadata?.tableName, usingTemplateNameAsTableName])

  const onSnowflakeChange = useCallback(
    (value, name) => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...assignment,
          [name]: value,
        },
      }))
    },
    [onChange, assignment],
  )

  const onSnowflakeMetadataChange = useCallback(
    (value, name) => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...assignment,
          metadata: {
            ...assignment.metadata,
            [name]: value,
          },
        },
      }))

      // Show validation errors when the user starts typing
      setShowValidation(true)
    },
    [onChange, assignment],
  )

  const hasErrors =
    assignment.metadata?.tableName === '' ||
    (assignment.metadata?.tableName?.length > 0 && validationErrors.length > 0)

  let tableName = assignment.metadata?.tableName
  if (usingTemplateNameAsTableName) {
    tableName = validateTableName(template.template.name).sanitized
  }

  useEffect(() => {
    onChange(old => ({
      ...old,
      [assignment.id]: {
        ...assignment,
        delimiter: ',',
        file_type: ['csv'],
        filename_pattern: 'output',
        include_summary_header: 0,
        /**
         * Snowflake outbound assignment requires Claravine ID to be included and read-only
         */
        included_output_fields: assignment.included_output_fields.map(field =>
          field.name === 'Claravine ID' ? { ...field, selected: true, readOnly: true } : field,
        ),
        ...(usingTemplateNameAsTableName && { metadata: { ...assignment.metadata, tableName } }),
      },
    }))
  }, [usingTemplateNameAsTableName, tableName])

  return (
    <FullWidth>
      <FileFormPartial integration={integration} assignment={assignment} isFileBased={false}>
        <AutoComplete
          label={t`Account`}
          selected={assignment.connector_id}
          onSelected={e => onSnowflakeChange(e, 'connector_id')}
          options={connectors || []}
          required
        />
        <CvsTextField
          label={t`Table name`}
          name="Table name"
          fullWidth
          error={hasErrors ? [''] : []}
          onChange={e => onSnowflakeMetadataChange(e.target.value, 'tableName')}
          value={assignment.metadata.tableName}
          defaultValue={tableName}
          required
        />
        {showValidation && <NameValidator validationErrors={validationErrors} />}
      </FileFormPartial>
      {showIntegrationNotifications && (
        <NotificationEmailConfig
          integrationAssignmentId={assignment.id}
          connectorId={assignment.connector_id}
        />
      )}
    </FullWidth>
  )
}

SnowflakeOutbound.propTypes = {
  assignment: object.isRequired,
  integration: object.isRequired,
}
